/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { getTableConfiguration } from '../../../../../utils/universalTracker'
import { UniversalTrackerPlain, ValueTable } from '@g17eco/types/universalTracker';
import { InputColumn, QuestionSubmitData, TableDataInfo } from '@g17eco/types/questionInterfaces';
import { ColumnMap, convertInputData, createConversionParams } from '@utils/valueDataTable';
import { getConvertedValue, getPopulatedConnectionValue, isSameValue } from '@utils/universalTrackerValue';
import UniversalTrackerClassInterface from '@models/UniversalTrackerClassInterface';

interface TableSubmitValues {
  submitValues: QuestionSubmitData;
  table?: TableDataInfo;
  universalTracker: UniversalTrackerClassInterface | Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>;
  shouldEnforceTable?: boolean;
}

export const updateTableDataSubmitValues = (submitData: TableSubmitValues) => {
  const { submitValues, table, universalTracker, shouldEnforceTable } = submitData;

  const tableConfig: ValueTable | undefined = getTableConfiguration(universalTracker, shouldEnforceTable);

  if (!tableConfig || !table || !Array.isArray(table.rows)) {
    return submitValues;
  }

  const { columns } = tableConfig;
  const mapCode: ColumnMap = new Map(columns.map((c) => [c.code, c]));

  const rowData = table.rows.filter((r) => !r.isRemoved).map((r) => r.data);

  submitValues.valueData.input.table = rowData.map((rowColumns) => {
    return rowColumns.map((col) => {
      const colMetadata = mapCode.get(col.code);
      if (!colMetadata) {
        return col;
      }
      const params = createConversionParams(colMetadata, col);
      const { isCurrency, defaultUnit, defaultNumberScale } = params;
      const unit = isCurrency ? undefined : (col.unit ?? defaultUnit);
      const convertedConnectionValue = getPopulatedConnectionValue(col.source, params);
      const convertedValue = getConvertedValue(params);
      return {
        ...col,
        source: isSameValue(convertedConnectionValue, convertedValue) ? col.source : undefined,
        numberScale: col.numberScale ?? defaultNumberScale,
        unit,
      };
    });
  });

  submitValues.valueData.table = convertInputData(rowData, mapCode);

  // [GU-6498] Write-back for backward compatibility
  // If the table has a designated valueColumn, we sync its value to the top-level scalar fields.
  if (tableConfig.valueColumn) {
    const inputTable = submitValues.valueData.input.table;
    const dataTable = submitValues.valueData.table;

    if (inputTable?.length > 0 && dataTable?.length > 0) {
      const inputRow = inputTable[0];
      const dataRow = dataTable[0];

      const inputCol = inputRow.find((c: InputColumn) => c.code === tableConfig.valueColumn);
      const dataCol = dataRow.find((c: InputColumn) => c.code === tableConfig.valueColumn);

      const scalarInputValue =
        inputCol?.value !== undefined && !Array.isArray(inputCol.value) ? inputCol.value : undefined;
      const scalarDataValue = dataCol?.value !== undefined && !Array.isArray(dataCol.value) ? dataCol.value : undefined;

      if (scalarInputValue !== undefined) {
        submitValues.value = scalarDataValue;
        submitValues.valueData.input.value = typeof scalarInputValue === 'number' ? scalarInputValue : undefined;

        // Sync unit and number scale for full backward compatibility
        if (inputCol?.unit) {
          submitValues.valueData.input.unit = inputCol.unit;
        }
        if (inputCol?.numberScale) {
          submitValues.valueData.input.numberScale = inputCol.numberScale;
        }
      }
    }
  }

  return submitValues;
}
